import { EditableCellProps } from "@components/EditableCell"
import { AppNode, NodeProperties } from "@features/builder/graph/types"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import { getCategoryDisplayNames } from "@features/builder/NodeDrawer/NodeForm/helpers"
import NodeDrawerAccordion from "@features/builder/NodeDrawer/NodeDrawerAccordion"
import { Add } from "@mui/icons-material"
import { Button, FormHelperText, Grid } from "@mui/material"
import { createNodeLabelMap, createNodeTypeMap, rowsToDfCpd } from "@utils/node"
import { useNodes } from "@xyflow/react"
import { useMemo } from "react"
import { useFormContext } from "react-hook-form"
import CategoricalNodeTable from "@features/builder/NodeDrawer/NodeForm/CategoricalNodeTable"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import { CUSTOM_ERROR_ATTRIBUTE } from "@utils/form"
import { errMsg } from "@features/builder/NodeDrawer/NodeForm/schema"

type CategoricalPropertiesTableAccordionProps = {
  nodeProperties: NodeProperties
  onPropertiesUpdate: (properties: NodeProperties) => void
  nodeId: string
}

const CategoricalPropertiesTableAccordion = (props: CategoricalPropertiesTableAccordionProps) => {
  const allNodes = useNodes<AppNode>()
  const labelMap = useMemo(() => createNodeLabelMap(allNodes), [allNodes])
  const nodeTypeMap = useMemo(() => createNodeTypeMap(allNodes), [allNodes])
  const form = useFormContext<NodeFormValues>()
  const { getNodeValidation } = useNodesValidation()

  const handleAddCategory = () => {
    const updatedProperties = structuredClone(props.nodeProperties)
    updatedProperties.df_cpd = rowsToDfCpd(form.getValues("properties"))

    const attributeLength = updatedProperties.df_cpd.distribution?.length ?? 0
    const attributeName = getNewAttributeName(updatedProperties)
    updatedProperties.df_cpd[attributeName] = Array(attributeLength).fill(0)

    props.onPropertiesUpdate(updatedProperties)

    form.setValue(
      "categoryDisplayNames",
      getCategoryDisplayNames(updatedProperties, form.getValues("categoryDisplayNames")),
      { shouldDirty: true, shouldValidate: form.formState.isSubmitted },
    )
  }

  const handleHeaderUpdate = (propertyName: string, newPropertyName: string) => {
    form.setValue(`categoryDisplayNames.${propertyName}`, newPropertyName, {
      shouldDirty: true,
      shouldValidate: false,
    })

    void form.trigger("categoryDisplayNames")
  }

  let handleRemoveCategory: EditableCellProps["onRemove"]

  if (!form.formState.disabled) {
    handleRemoveCategory = (value) => {
      if (!value) return

      const propertyName = value.toString()

      const updatedProperties = structuredClone(props.nodeProperties)
      updatedProperties.df_cpd = rowsToDfCpd(form.getValues("properties"))
      delete updatedProperties.df_cpd[propertyName]

      // prevent deleting last category - the only key left will be { distribution: ["Categorical"] }
      if (Object.keys(updatedProperties.df_cpd).length === 1) {
        form.setError("categoryDisplayNames", {
          type: "manual",
          message: errMsg.CATEGORY_NAME_CANT_DELETE_LAST,
        })
        return
      }

      props.onPropertiesUpdate(updatedProperties)

      form.setValue(
        "categoryDisplayNames",
        getCategoryDisplayNames(updatedProperties, form.getValues("categoryDisplayNames")),
        { shouldDirty: true, shouldValidate: form.formState.isSubmitted },
      )
    }
  }

  const dfcpdErrors = getNodeValidation(props.nodeId, "CATEGORICAL_PROPERTIES")?.errors ?? []
  const categoryDisplayNamesError = form.formState.errors.categoryDisplayNames
  const categoryErrorMessage = categoryDisplayNamesError?.message

  return (
    <NodeDrawerAccordion id="properties-table" label="Properties table">
      <Grid container item gap={1}>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddCategory}
            disabled={form.formState.disabled}
          >
            Add category
          </Button>
        </Grid>
      </Grid>

      <Grid item>
        <CategoricalNodeTable
          nodeLabelMap={labelMap}
          nodeTypeMap={nodeTypeMap}
          nodeProperties={props.nodeProperties}
          onHeaderUpdate={handleHeaderUpdate}
          onCategoryRemove={handleRemoveCategory}
          validationErrors={dfcpdErrors}
        />
      </Grid>

      {categoryErrorMessage && (
        <Grid item>
          {categoryErrorMessage && (
            <FormHelperText error {...{ [CUSTOM_ERROR_ATTRIBUTE]: true }}>
              - {categoryErrorMessage}
            </FormHelperText>
          )}
        </Grid>
      )}
    </NodeDrawerAccordion>
  )
}

export default CategoricalPropertiesTableAccordion

const getNewAttributeName = (properties: NodeProperties): string => {
  const propertyBase = "category"
  let i = 1

  while (properties.df_cpd[`${propertyBase}${i}`]) {
    i++
  }

  return `${propertyBase}${i}`
}
